// DS: DS4.3
import React, { AriaAttributes } from 'react';
import c from 'classnames';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import PlainButtonOrLink, {
  Props as PlainButtonOrLinkProps,
} from '../../../Buttons/PlainButtonOrLink';
import s from './MainToggle.scss';

interface BaseProps {
  value: boolean;
  size: 'small' | 'medium' | 'large';
  variant: 'main' | 'dreamup-purple';
  id?: string;
  name?: string;
  hasError?: boolean;
  disabled?: boolean;
  biData?: BiEvent;

  className?: string;
  onChange: (value: boolean) => void;
  innerRef?: (node: HTMLElement | null) => void;
  // These aria label props are duplicated because typescript doesn't like us trying to require one or the other.
  'aria-label'?: AriaAttributes['aria-label'];
  'aria-labelledby'?: AriaAttributes['aria-labelledby'];
  type?: PlainButtonOrLinkProps['type'];
  tabIndex?: number;
}

interface PropsWithAriaLabel extends BaseProps {
  /** We require aria-label or aria-labelledby since the spec for this component doesn't include descriptive text, which screen readers will need */
  'aria-label': AriaAttributes['aria-label'];
}

interface PropsWithAriaLabelledBy extends BaseProps {
  /** We require aria-label or aria-labelledby since the spec for this component doesn't include descriptive text, which screen readers will need */
  'aria-labelledby': AriaAttributes['aria-labelledby'];
}

interface PropsWithDevLabelAcknowledgement extends BaseProps {
  hasAccessibleLabel: true;
}

export type Props =
  | PropsWithAriaLabel
  | PropsWithAriaLabelledBy
  | PropsWithDevLabelAcknowledgement;

/**
 * A switch/toggle.
 *
 * `aria-label` or `aria-labelledby` is required since this component doesn't have a text label in the specification. If you
 * see a type error mention one of these, please add one depending on context.
 */
const MainToggle: React.FC<Props> = ({
  id,
  name,
  variant,
  value,
  size,
  hasError,
  disabled,
  biData,
  className,
  innerRef,
  onChange,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  type: buttonType,
  tabIndex,
}) => {
  const isDisabled = disabled || hasError;
  return (
    <PlainButtonOrLink
      id={id}
      name={name}
      className={c(
        className,
        s['root'],
        value ? s['on'] : s['off'],
        isDisabled && s['disabled'],
        hasError && s['error'],
        s[`variant-${variant}`],
        s[size]
      )}
      innerRef={innerRef}
      onClick={() => onChange(!value)}
      biData={biData}
      disabled={isDisabled}
      aria-pressed={value}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      type={buttonType}
      tabIndex={tabIndex}
    >
      <span className={s['dot']}>
        {hasError && <span className={s['dot-line']} />}
      </span>
    </PlainButtonOrLink>
  );
};

MainToggle.displayName = 'MainToggle';

export default MainToggle;
